import masterData from '../data/master_data.json';

export interface LocationData {
  country: any;
  state?: any;
  city?: any;
  breadcrumbs: Array<{name: string, slug: string, path: string}>;
  currencySymbol: string;
  locationName: string;
  content: any;
  nearbyCities: Array<{name: string, slug: string, path: string, distance?: string}>;
}

export class RoutingEngine {
  private data: any;

  constructor() {
    this.data = masterData;
  }

  parseLocation(pathname: string): LocationData | null {
    const parts = pathname.split('/').filter(Boolean);
    
    if (parts.length === 0) {
      return this.getDefaultLocation();
    }

    const countrySlug = parts[0];
    const country = this.data.countries[countrySlug];

    if (!country) {
      return this.getDefaultLocation();
    }

    const breadcrumbs: Array<{name: string, slug: string, path: string}> = [
      { name: 'Home', slug: 'home', path: '/' },
      { name: country.name, slug: countrySlug, path: `/${countrySlug}` }
    ];

    // Country level only
    if (parts.length === 1) {
      return {
        country,
        breadcrumbs,
        currencySymbol: country.currency_symbol,
        locationName: country.name,
        content: this.getDefaultContent(country.name),
        nearbyCities: this.getCountryLevelCities(country)
      };
    }

    // State/Region level
    const stateSlug = parts[1];
    const state = country.states?.[stateSlug] || country.regions?.[stateSlug];

    if (!state) {
      return {
        country,
        breadcrumbs,
        currencySymbol: country.currency_symbol,
        locationName: country.name,
        content: this.getDefaultContent(country.name),
        nearbyCities: this.getCountryLevelCities(country)
      };
    }

    breadcrumbs.push({
      name: state.name,
      slug: stateSlug,
      path: `/${countrySlug}/${stateSlug}`
    });

    // State level only
    if (parts.length === 2) {
      return {
        country,
        state,
        breadcrumbs,
        currencySymbol: country.currency_symbol,
        locationName: `${state.name}, ${country.name}`,
        content: this.getDefaultContent(state.name),
        nearbyCities: this.getStateLevelCities(country, countrySlug, state, stateSlug)
      };
    }

    // City level
    const citySlug = parts[2];
    const city = state.cities?.[citySlug];

    if (!city) {
      return {
        country,
        state,
        breadcrumbs,
        currencySymbol: country.currency_symbol,
        locationName: `${state.name}, ${country.name}`,
        content: this.getDefaultContent(state.name),
        nearbyCities: this.getStateLevelCities(country, countrySlug, state, stateSlug)
      };
    }

    breadcrumbs.push({
      name: city.name,
      slug: citySlug,
      path: `/${countrySlug}/${stateSlug}/${citySlug}`
    });

    return {
      country,
      state,
      city,
      breadcrumbs,
      currencySymbol: country.currency_symbol,
      locationName: `${city.name}, ${state.name}`,
      content: city.content,
      nearbyCities: this.getNearbyCities(country, countrySlug, state, stateSlug, city)
    };
  }

  private getDefaultLocation(): LocationData {
    const usa = this.data.countries.usa;
    return {
      country: usa,
      breadcrumbs: [
        { name: 'Home', slug: 'home', path: '/' },
        { name: 'United States', slug: 'usa', path: '/usa' }
      ],
      currencySymbol: '$',
      locationName: 'United States',
      content: this.getDefaultContent('United States'),
      nearbyCities: this.getCountryLevelCities(usa)
    };
  }

  private getDefaultContent(locationName: string): any {
    return {
      intro: `Calculate your take-home salary in ${locationName} with our comprehensive tax calculator. Get accurate estimates of federal, state, and local taxes.`,
      fun_fact: `${locationName} offers diverse employment opportunities across multiple industries.`,
      hiring_stats: 'Understanding your net income helps you make informed financial decisions.'
    };
  }

  private getNearbyCities(country: any, countrySlug: string, state: any, stateSlug: string, city: any): Array<{name: string, slug: string, path: string, distance?: string}> {
    const nearbyCities: Array<{name: string, slug: string, path: string, distance?: string}> = [];
    
    if (city.nearby_cities && state.cities) {
      city.nearby_cities.forEach((nearbySlug: string, index: number) => {
        const nearbyCity = state.cities[nearbySlug];
        if (nearbyCity) {
          nearbyCities.push({
            name: nearbyCity.name,
            slug: nearbySlug,
            path: `/${countrySlug}/${stateSlug}/${nearbySlug}`,
            distance: `${(index + 1) * 5} miles`
          });
        }
      });
    }

    return nearbyCities;
  }

  private getStateLevelCities(country: any, countrySlug: string, state: any, stateSlug: string): Array<{name: string, slug: string, path: string}> {
    const cities: Array<{name: string, slug: string, path: string}> = [];
    
    if (state.cities) {
      Object.keys(state.cities).slice(0, 6).forEach(citySlug => {
        const city = state.cities[citySlug];
        cities.push({
          name: city.name,
          slug: citySlug,
          path: `/${countrySlug}/${stateSlug}/${citySlug}`
        });
      });
    }

    return cities;
  }

  private getCountryLevelCities(country: any): Array<{name: string, slug: string, path: string}> {
    const cities: Array<{name: string, slug: string, path: string}> = [];
    const states = country.states || country.regions || {};
    
    Object.keys(states).forEach(stateSlug => {
      const state = states[stateSlug];
      if (state.cities) {
        Object.keys(state.cities).slice(0, 2).forEach(citySlug => {
          const city = state.cities[citySlug];
          cities.push({
            name: `${city.name}, ${state.name}`,
            slug: citySlug,
            path: `/${country.slug}/${stateSlug}/${citySlug}`
          });
        });
      }
    });

    return cities.slice(0, 6);
  }

  getAllLocations(): Array<{path: string, name: string}> {
    const locations: Array<{path: string, name: string}> = [];

    Object.keys(this.data.countries).forEach(countrySlug => {
      const country = this.data.countries[countrySlug];
      const states = country.states || country.regions || {};

      Object.keys(states).forEach(stateSlug => {
        const state = states[stateSlug];
        if (state.cities) {
          Object.keys(state.cities).forEach(citySlug => {
            const city = state.cities[citySlug];
            locations.push({
              path: `/${countrySlug}/${stateSlug}/${citySlug}`,
              name: `${city.name}, ${state.name}, ${country.name}`
            });
          });
        }
      });
    });

    return locations;
  }
}